import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Target, 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign,
  Clock,
  ArrowLeft,
  Edit,
  Trash2,
  Share2,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const token = localStorage.getItem('ngoToken');
        if (!token) {
          toast.error('Authentication required');
          navigate('/ngo/login');
          return;
        }

        // Try multiple possible endpoints to handle different server configurations
        const possibleEndpoints = [
          `http://localhost:3011/api/projects/${id}`,
          `http://localhost:5000/api/projects/${id}`,
          `https://bloodbackend-99n2.onrender.com/api/projects/${id}`
        ];
        
        let response = null;
        let error = null;
        
        // Try each endpoint until one works
        for (const endpoint of possibleEndpoints) {
          try {
            console.log(`Trying to fetch project details from endpoint: ${endpoint}`);
            
            response = await axios.get(endpoint, {
              headers: {
                'Authorization': `Bearer ${token}`
              },
              timeout: 5000 // 5 second timeout
            });
            
            // If we get here, the request was successful
            console.log('Project details fetched successfully:', response.data);
            break;
          } catch (err) {
            console.log(`Endpoint ${endpoint} failed:`, err.message);
            error = err;
            // Continue to next endpoint
          }
        }
        
        // If we got a successful response, use the data
        if (response && response.data) {
          setProject(response.data);
          setLoading(false);
          return;
        }
        
        // If all endpoints failed, try to use a mock project
        console.warn('All API endpoints failed, using fallback data');
        
        // Create a mock project with the ID from the URL
        const mockProject = {
          _id: id,
          title: 'Project Details',
          category: 'Environment',
          location: 'Maharashtra',
          startDate: new Date().toISOString(),
          endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString(),
          goalType: 'Fundraising',
          goalAmount: 50000,
          raisedAmount: 12000,
          beneficiaries: 'Local Community',
          status: 'active',
          description: 'This is a placeholder for the project details. The actual project data could not be loaded from the server.',
          image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
          callToAction: {
            buttonText: 'Support Now',
            buttonLink: ''
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        setProject(mockProject);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching project details:', error);
        setError('Failed to load project details');
        setLoading(false);
      }
    };

    if (id) {
      fetchProjectDetails();
    } else {
      setError('Project ID is missing');
      setLoading(false);
    }
  }, [id, navigate]);

  const handleDeleteProject = async () => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('ngoToken');
        
        if (!token) {
          toast.error('Authentication required');
          setLoading(false);
          return;
        }
        
        console.log(`Attempting to delete project with ID: ${id}`);
        
        // Primary endpoint - try this first
        const primaryEndpoint = `http://localhost:3011/api/projects/${id}`;
        
        try {
          console.log(`Sending delete request to: ${primaryEndpoint}`);
          console.log('Authorization token:', `Bearer ${token}`);
          
          const response = await axios.delete(primaryEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            timeout: 15000 // 15 second timeout for deletion
          });
          
          console.log('Delete response:', response);
          
          if (response.status === 200 && response.data && response.data.success) {
            console.log('Project deleted successfully from backend');
            toast.success('Project deleted successfully');
            navigate('/ngo/dashboard/projects');
          } else {
            console.warn('Unexpected response format:', response.data);
            toast.error('Unexpected response from server');
            setLoading(false);
          }
        } catch (error) {
          console.error('Error deleting project:', error);
          setLoading(false);
          
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error('Error response data:', error.response.data);
            console.error('Error response status:', error.response.status);
            
            if (error.response.status === 401) {
              toast.error('Not authorized to delete this project');
            } else if (error.response.status === 404) {
              toast.error('Project not found');
              // Navigate away even if project not found
              navigate('/ngo/dashboard/projects');
            } else {
              toast.error(`Server error: ${error.response.data.msg || 'Unknown error'}`);
            }
          } else if (error.request) {
            // The request was made but no response was received
            console.error('No response received:', error.request);
            toast.error('No response from server. Please try again later.');
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error setting up request:', error.message);
            toast.error(`Error: ${error.message}`);
          }
        }
      } catch (error) {
        console.error('Unexpected error in delete handler:', error);
        toast.error('An unexpected error occurred');
        setLoading(false);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate progress percentage
  const calculateProgress = (raised, goal) => {
    if (!goal || goal === 0) return 0;
    const percentage = (raised / goal) * 100;
    return Math.min(100, percentage); // Cap at 100%
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Project not found!</strong>
        <span className="block sm:inline"> The requested project could not be found.</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/ngo/dashboard/projects')}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Projects
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="h-64 overflow-hidden relative">
          <img 
            src={project.image} 
            alt={project.title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 right-4 flex space-x-2">
            <button 
              onClick={() => navigate(`/ngo/dashboard/projects/edit/${project._id}`)}
              className="p-2 bg-white rounded-full shadow-md hover:bg-blue-50 text-blue-600"
              title="Edit Project"
            >
              <Edit size={18} />
            </button>
            <button 
              onClick={handleDeleteProject}
              className="p-2 bg-white rounded-full shadow-md hover:bg-red-50 text-red-600"
              title="Delete Project"
            >
              <Trash2 size={18} />
            </button>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success('Link copied to clipboard');
              }}
              className="p-2 bg-white rounded-full shadow-md hover:bg-green-50 text-green-600"
              title="Share Project"
            >
              <Share2 size={18} />
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
            <div className="inline-block px-2 py-1 text-xs font-semibold text-white bg-blue-600 rounded-full mb-2">
              {project.category}
            </div>
            <h1 className="text-2xl font-bold text-white">{project.title}</h1>
          </div>
        </div>

        <div className="p-6">
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center text-gray-600 dark:text-gray-300">
              <MapPin size={18} className="mr-2" />
              <span>{project.location}</span>
            </div>
            <div className="flex items-center text-gray-600 dark:text-gray-300">
              <Calendar size={18} className="mr-2" />
              <span>{formatDate(project.startDate)} - {formatDate(project.endDate)}</span>
            </div>
            <div className="flex items-center text-gray-600 dark:text-gray-300">
              <Users size={18} className="mr-2" />
              <span>{project.beneficiaries}</span>
            </div>
            <div className="flex items-center text-gray-600 dark:text-gray-300">
              <Clock size={18} className="mr-2" />
              <span>
                {new Date(project.endDate) > new Date() 
                  ? `${Math.ceil((new Date(project.endDate) - new Date()) / (1000 * 60 * 60 * 24))} days left` 
                  : 'Completed'}
              </span>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">About this Project</h2>
            <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line">
              {project.description}
            </p>
          </div>

          {project.goalType === 'Fundraising' && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Fundraising Goal</h2>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  <DollarSign size={18} className="inline mr-1" />
                  ₹{project.raisedAmount.toLocaleString()} raised
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  of ₹{project.goalAmount.toLocaleString()} goal
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700 mb-2">
                <div 
                  className="bg-blue-600 h-3 rounded-full" 
                  style={{ width: `${calculateProgress(project.raisedAmount, project.goalAmount)}%` }}
                ></div>
              </div>
              <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                {calculateProgress(project.raisedAmount, project.goalAmount).toFixed(1)}% complete
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium 
                ${project.status === 'active' ? 'bg-green-100 text-green-800' : 
                project.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 
                project.status === 'completed' ? 'bg-blue-100 text-blue-800' : 
                'bg-gray-100 text-gray-800'}">
                {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
              </span>
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={() => navigate(`/ngo/dashboard/projects/edit/${project._id}`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              >
                <Edit size={16} className="mr-2" />
                Edit Project
              </button>
              {project.callToAction && (
                <a 
                  href={project.callToAction.buttonLink || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                >
                  <ExternalLink size={16} className="mr-2" />
                  {project.callToAction.buttonText || 'Support Now'}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
